import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TrendingUp, Briefcase, User, Upload, ArrowUpRight, Globe, Calendar, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS_BIZ = ['#D36135', '#E8A365', '#C4553A', '#F2C078', '#A84420', '#E09050', '#8B3A1F', '#D4A574'];
const COLORS_PER = ['#284A33', '#4A7A5D', '#6B8E7B', '#3D6B4A', '#8AAF98', '#1C3624', '#5A8068', '#A3C4B0'];

const fmtINR = (v) => {
  if (v == null || isNaN(v)) return '0';
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const REGION_LABELS = { india: 'India (Apr-Mar)', uae: 'UAE (May-Apr)' };

function SummaryCard({ testId, icon: Icon, label, value, accent, onClick, subtitle }) {
  return (
    <div data-testid={testId} onClick={onClick}
      className={`bg-white border border-[#E2E0D8] rounded-md p-5 transition-all duration-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] ${onClick ? 'cursor-pointer hover:border-[#284A33]/30' : ''}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="p-1.5 rounded-md" style={{ backgroundColor: `${accent || '#284A33'}15` }}>
          <Icon size={16} style={{ color: accent || '#284A33' }} />
        </div>
        <span className="text-[11px] tracking-[0.15em] uppercase text-[#5C5C55]">{label}</span>
      </div>
      <p className="font-mono text-2xl tracking-tighter text-[#1C1C19]">{value}</p>
      {subtitle && <p className="text-[10px] text-[#5C5C55] mt-1">{subtitle}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [businessCats, setBusinessCats] = useState([]);
  const [personalCats, setPersonalCats] = useState([]);
  const [trends, setTrends] = useState([]);
  const [fyData, setFyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState('all');
  const [fy, setFy] = useState(() => {
    const now = new Date();
    const m = now.getMonth() + 1;
    const y = now.getFullYear();
    const startYear = m >= 4 ? y : y - 1;
    return `FY ${startYear}-${String(startYear + 1).slice(2)}`;
  });
  const [fyOptions, setFyOptions] = useState({ india: [], uae: [] });
  const [dashTab, setDashTab] = useState('business');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const { data } = await axios.get(`${API}/transactions/filters`, { withCredentials: true });
        setFyOptions(data.financial_years || { india: [], uae: [] });
      } catch (err) { console.error('Filter fetch error:', err); }
    };
    fetchFilters();
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (region && region !== 'all') params.region = region;
      if (fy && fy !== 'all') params.fy = fy;

      const [sumR, catR, trR, fyR] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { params, withCredentials: true }),
        axios.get(`${API}/dashboard/by-category`, { params, withCredentials: true }),
        axios.get(`${API}/dashboard/trends`, { params, withCredentials: true }),
        axios.get(`${API}/dashboard/by-fy`, { params: { region: region !== 'all' ? region : 'india' }, withCredentials: true }),
      ]);
      setSummary(sumR.data);
      const catData = catR.data;
      setBusinessCats(catData.filter(c => c._id?.type === 'business').map(c => ({ name: c._id?.category || 'Other', value: c.total, count: c.count })).sort((a, b) => b.value - a.value));
      setPersonalCats(catData.filter(c => c._id?.type === 'personal').map(c => ({ name: c._id?.category || 'Other', value: c.total, count: c.count })).sort((a, b) => b.value - a.value));
      setTrends(trR.data.map(t => ({ month: t._id, total: t.total, business: t.business, personal: t.personal })));
      setFyData(fyR.data.map(f => ({ fy: f._id, total: f.total, business: f.business, personal: f.personal, count: f.count })));
    } catch (err) { console.error('Dashboard error:', err); }
    finally { setLoading(false); }
  }, [region, fy]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const activeFyList = region === 'uae' ? fyOptions.uae : fyOptions.india;

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-full">
      <div className="text-[#5C5C55] text-sm">Loading dashboard...</div>
    </div>
  );

  const hasData = summary && summary.count > 0;
  const isBiz = dashTab === 'business';
  const activeCats = isBiz ? businessCats : personalCats;
  const activeColors = isBiz ? COLORS_BIZ : COLORS_PER;
  const activeAccent = isBiz ? '#D36135' : '#4A7A5D';
  const activeFyKey = isBiz ? 'business' : 'personal';
  const activeTrendKey = isBiz ? 'business' : 'personal';
  const activeLabel = isBiz ? 'Business' : 'Personal';
  const activeTotal = isBiz ? (summary?.business_spend || 0) : (summary?.personal_spend || 0);

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Dashboard</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Globe size={14} className="text-[#5C5C55]" />
            <Select value={region} onValueChange={(v) => { setRegion(v); setFy('all'); }}>
              <SelectTrigger data-testid="filter-region" className="w-[170px] h-8 border-[#E2E0D8] text-xs">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                <SelectItem value="india">India (Apr-Mar)</SelectItem>
                <SelectItem value="uae">UAE (May-Apr)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-[#5C5C55]" />
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger data-testid="filter-fy" className="w-[140px] h-8 border-[#E2E0D8] text-xs">
                <SelectValue placeholder="All FYs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {activeFyList.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {hasData && (
            <button onClick={() => navigate('/upload')} data-testid="dashboard-upload-btn"
              className="flex items-center gap-2 bg-[#284A33] text-white hover:bg-[#1C3624] px-4 py-1.5 rounded-md text-xs font-medium transition-colors">
              <Upload size={13} /> Upload
            </button>
          )}
        </div>
      </div>

      {/* Active filters */}
      {(region !== 'all' || fy !== 'all') && (
        <div className="flex items-center gap-2 text-xs" data-testid="active-filters">
          <span className="text-[#5C5C55]">Showing:</span>
          {region !== 'all' && <span className="bg-[#284A33]/10 text-[#284A33] px-2 py-0.5 rounded font-mono uppercase tracking-wide">{REGION_LABELS[region] || region}</span>}
          {fy !== 'all' && <span className="bg-[#D36135]/10 text-[#D36135] px-2 py-0.5 rounded font-mono uppercase tracking-wide">{fy}</span>}
          <button onClick={() => { setRegion('all'); setFy('all'); }} className="text-[#5C5C55] hover:text-[#1C1C19] underline ml-1" data-testid="clear-filters">Clear</button>
        </div>
      )}

      {/* Business / Personal Tabs */}
      <div className="flex items-center gap-1 bg-[#E8E6DE] p-1 rounded-lg w-fit">
        <button data-testid="dash-tab-business" onClick={() => setDashTab('business')}
          className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${dashTab === 'business' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <Briefcase size={14} /> Business
        </button>
        <button data-testid="dash-tab-personal" onClick={() => setDashTab('personal')}
          className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${dashTab === 'personal' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <User size={14} /> Personal
        </button>
      </div>

      {/* Summary Cards — contextual to selected tab */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isBiz ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4`}>
        {isBiz ? (
          <>
            <SummaryCard testId="business-spend-card" icon={Briefcase} label="Business Spend" value={`₹${fmtINR(summary?.business_spend)}`} accent="#D36135"
              onClick={() => navigate('/transactions?tag=business')} />
            <SummaryCard testId="income-card" icon={TrendingUp} label="Income" value={`₹${fmtINR(summary?.income_total)}`} accent="#2563EB"
              subtitle={summary?.income_count > 0 ? `${summary.income_count} inflows` : 'Tag credits as Income'}
              onClick={() => navigate('/transactions?tag=income')} />
            <SummaryCard testId="reimbursable-card" icon={Building2} label="Dubai Co. Claimable" value={`₹${fmtINR(summary?.reimbursable_spend)}`} accent="#8B5CF6"
              subtitle={summary?.reimbursable_count > 0 ? `${summary.reimbursable_count} UAE+Business txns` : 'Tag UAE entries as Business'}
              onClick={() => navigate('/transactions?region=uae&tag=business')} />
          </>
        ) : (
          <>
            <SummaryCard testId="personal-spend-card" icon={User} label="Personal Spend" value={`₹${fmtINR(summary?.personal_spend)}`} accent="#4A7A5D"
              onClick={() => navigate('/transactions?tag=personal')} />
            <SummaryCard testId="income-card" icon={TrendingUp} label="Income" value={`₹${fmtINR(summary?.income_total)}`} accent="#2563EB"
              subtitle={summary?.income_count > 0 ? `${summary.income_count} inflows` : 'Tag credits as Income'}
              onClick={() => navigate('/transactions?tag=income')} />
          </>
        )}
      </div>

      {!hasData ? (
        <div className="bg-white border border-[#E2E0D8] rounded-md p-16 text-center">
          <Upload className="mx-auto mb-4 text-[#D5D2C6]" size={48} />
          <h3 className="font-heading text-xl text-[#1C1C19] mb-2">
            {region !== 'all' || fy !== 'all' ? 'No Data for Selected Filters' : 'No Data Yet'}
          </h3>
          <p className="text-[#5C5C55] mb-6 text-sm">
            {region !== 'all' || fy !== 'all' ? 'Try changing the region or financial year filter' : 'Upload your first statement to see your expense dashboard'}
          </p>
          {region === 'all' && fy === 'all' && (
            <button onClick={() => navigate('/upload')} data-testid="dashboard-upload-link"
              className="inline-flex items-center gap-2 bg-[#284A33] text-white hover:bg-[#1C3624] px-6 py-2.5 rounded-md font-medium transition-colors text-sm">
              Upload Statement <ArrowUpRight size={15} />
            </button>
          )}
        </div>
      ) : (
        <>
          {/* FY Chart — full width for active tab */}
          {fyData.length > 0 && (
            <div className="bg-white border border-[#E2E0D8] rounded-md p-5" data-testid={`fy-${dashTab}-chart`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1 rounded-md" style={{ backgroundColor: `${activeAccent}15` }}>
                  {isBiz ? <Briefcase size={14} style={{ color: activeAccent }} /> : <User size={14} style={{ color: activeAccent }} />}
                </div>
                <h3 className="font-heading text-sm font-medium text-[#1C1C19]">{activeLabel} Spending by Financial Year</h3>
                <span className="ml-auto text-xs font-mono text-[#5C5C55]">
                  ₹{fmtINR(fyData.reduce((s, f) => s + (f[activeFyKey] || 0), 0))}
                </span>
              </div>
              <p className="text-[10px] text-[#5C5C55] mb-3 ml-7">
                {region === 'uae' ? 'UAE (May-Apr)' : 'India (Apr-Mar)'}
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={fyData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
                  <XAxis dataKey="fy" tick={{ fill: '#5C5C55', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5C5C55', fontSize: 11 }} tickFormatter={(v) => `₹${fmtINR(v)}`} />
                  <Tooltip formatter={(v) => `₹${fmtINR(v)}`} contentStyle={{ borderRadius: '6px', border: '1px solid #E2E0D8', fontSize: '12px' }} />
                  <Bar dataKey={activeFyKey} name={activeLabel} fill={activeAccent} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Category Pie — full width for active tab */}
          <div className="bg-white border border-[#E2E0D8] rounded-md p-5" data-testid={`${dashTab}-category-chart`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1 rounded-md" style={{ backgroundColor: `${activeAccent}15` }}>
                {isBiz ? <Briefcase size={14} style={{ color: activeAccent }} /> : <User size={14} style={{ color: activeAccent }} />}
              </div>
              <h3 className="font-heading text-sm font-medium text-[#1C1C19]">{activeLabel} Spending by Category</h3>
              <span className="ml-auto text-xs font-mono text-[#5C5C55]">
                ₹{fmtINR(activeTotal)}
              </span>
            </div>
            {activeCats.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={activeCats} cx="50%" cy="50%" innerRadius={65} outerRadius={120} dataKey="value" nameKey="name" paddingAngle={2}>
                      {activeCats.map((_, i) => <Cell key={i} fill={activeColors[i % activeColors.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `₹${fmtINR(v)}`} contentStyle={{ borderRadius: '6px', border: '1px solid #E2E0D8', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {activeCats.map((c, i) => (
                    <div key={c.name} className="flex items-center gap-2 text-xs">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: activeColors[i % activeColors.length] }} />
                      <span className="text-[#1C1C19] truncate flex-1" title={c.name}>{c.name}</span>
                      <span className="font-mono text-[#5C5C55] shrink-0">₹{fmtINR(c.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-[#5C5C55]">No {activeLabel.toLowerCase()} expenses</p>}
          </div>

          {/* Monthly Trends — for active tab */}
          {trends.length > 0 && (
            <div className="bg-white border border-[#E2E0D8] rounded-md p-6" data-testid={`${dashTab}-trends-chart`}>
              <h3 className="font-heading text-base font-medium text-[#1C1C19] mb-4">{activeLabel} Monthly Trends</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
                  <XAxis dataKey="month" tick={{ fill: '#5C5C55', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5C5C55', fontSize: 11 }} tickFormatter={(v) => `₹${fmtINR(v)}`} />
                  <Tooltip formatter={(v) => `₹${fmtINR(v)}`} contentStyle={{ borderRadius: '6px', border: '1px solid #E2E0D8', fontSize: '12px' }} />
                  <Area type="monotone" dataKey={activeTrendKey} stroke={activeAccent} fill={activeAccent} fillOpacity={0.08} strokeWidth={2} name={activeLabel} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
